import { useCallback, useEffect } from 'react';

import { apiService } from '../services/api.services';
import { useCompanyStore } from '../store/companyStore';
import { useUserStore } from '../store/userStore';
import type { AuthData, User } from '../types';
import { clearCpfHashStorage, readCpfFromHashStorage } from '../utils/authHash';
import { clearEntraIdIdentity, readEntraIdIdentity } from '../utils/entraIdSession';
import { resolveUserDisplayName } from '../utils/entraIdUserMapper';
import { buildPath } from '../utils/routeUtils';

let bootstrapSessionPromise: Promise<void> | null = null;
let bootstrapSessionDone = false;

// These routes are entry/special flows where automatic session bootstrap can break UX:
// - /loginsso: Entra ID owns the page (auto-detect + OIDC callback).
// - /forgot-userkey: recovery page is public and should not force validate/refresh.
// - /unauthorized: dedicated error page, should not trigger validate/refresh side effects.
const isAuthFlowPath = (pathname: string): boolean => {
  return ['/loginsso', '/forgot-userkey', '/unauthorized'].some((segment) =>
    pathname.includes(segment),
  );
};

// Custom hook for authentication using Zustand with HTTP-only cookies
export const useAuth = () => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    setUser,
    setLoading,
    clearError: clearStoreError,
    logout: logoutUser
  } = useUserStore();

  // Set authentication data (called by forms after successful API call)
  const setAuthData = useCallback((authData: AuthData) => {
    // Save authentication data in store
    setUser(authData.user);
  }, [setUser]);

  // Logout
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      // Call the real logout API
      await apiService.auth.postLogout();
    } catch (error) {
      // Logout error handled silently - still proceed with local logout
      console.warn('Logout API call failed:', error);
    } finally {
      useCompanyStore.getState().clearCompanySession();
      // Always clear local state and redirect
      logoutUser();
      clearCpfHashStorage();
      clearEntraIdIdentity();
      setLoading(false);
      window.location.href = buildPath('/login');
    }
  }, [logoutUser, setLoading]);

  // Merge into current user in store (avoids stale closure after await, e.g. switching card on CompanyPage)
  const updateUser = useCallback((updates: Partial<User>) => {
    useUserStore.getState().updateUser(updates);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    clearStoreError();
  }, [clearStoreError]);

  const validateSession = useCallback(async (): Promise<boolean> => {
    const response = await apiService.auth.getTokenSession();

    // Full payload path (preferred): hydrate complete user data.
    if (response && response.pessoaFisicaResponse && response.idPessoa != null && response.cpf) {
      setUser({
        idPessoa: Number(response.idPessoa),
        cpf: response.cpf,
        pessoaFisicaResponse: response.pessoaFisicaResponse,
      });
      return true;
    }

    // Explicit invalid-session payload from backend.
    if (response && response.success === false) {
      return false;
    }

    // CPF fallback path: if backend returns cpf (even without token or pessoaFisicaResponse),
    // hydrate from CADP by calling `postGetPhysicalPersonByCpf`.
    if (response?.cpf) {
      const cpfDigits = String(response.cpf).replace(/\D/g, '');
      if (!cpfDigits) {
        return false;
      }
      const pessoaFisicaResponse = await apiService.policyholder.postGetPhysicalPersonByCpf(cpfDigits);
      const pessoaFisica = pessoaFisicaResponse?.pessoaFisica;
      if (!pessoaFisica) {
        return false;
      }
      const idPessoa = Number(response?.idPessoa ?? pessoaFisica.id);
      if (!Number.isFinite(idPessoa) || idPessoa <= 0) {
        return false;
      }
      setUser({
        idPessoa,
        cpf: cpfDigits,
        pessoaFisicaResponse: {
          base: pessoaFisicaResponse.base ?? '',
          mensagem: pessoaFisicaResponse.mensagem ?? null,
          pessoaFisica: {
            id: pessoaFisica.id,
            nome: pessoaFisica.nome,
            cpf: pessoaFisica.cpf,
            dataNascimento: pessoaFisica.dataNascimento ?? '',
            nomeMae: pessoaFisica.nomeMae ?? '',
            sexo: pessoaFisica.sexo ?? '',
            estadoCivilId: pessoaFisica.estadoCivilId ?? '',
            grauEscolaridadeId: pessoaFisica.grauEscolaridadeId ?? '',
          },
        },
      });
      return true;
    }

    // Session-valid fallback path: backend replied `success: true` but with empty body.
    // Entra ID identity (chave M / e-mail) is restored without CADP id/CPF.
    // CADP still recovers encrypted CPF from localStorage `hash` and calls login-final.
    if (response?.success === true) {
      const entraUser = readEntraIdIdentity();
      if (entraUser) {
        useCompanyStore.getState().setSessionRole('BACKOFFICE');
        setUser(entraUser);
        return true;
      }
      const cpfFromHash = readCpfFromHashStorage();
      if (!cpfFromHash) {
        return false;
      }
      const loginFinalResponse = await apiService.login.loginFinal(cpfFromHash);
      const pessoaFisica = loginFinalResponse?.pessoaFisicaResponse?.pessoaFisica;
      if (!pessoaFisica || !loginFinalResponse?.cpf || loginFinalResponse?.idPessoa == null) {
        return false;
      }
      const idPessoa = Number(loginFinalResponse.idPessoa);
      if (!Number.isFinite(idPessoa) || idPessoa <= 0) {
        return false;
      }
      setUser({
        idPessoa,
        cpf: loginFinalResponse.cpf,
        pessoaFisicaResponse: loginFinalResponse.pessoaFisicaResponse,
      });
      return true;
    }

    return false;
  }, [setUser]);

  // Unauthorized handler without refresh-token flow
  const handleUnauthorized = useCallback(async () => {
    const currentPath = window.location.pathname;
    const loginPath = buildPath('/login');

    // Define auth-related endpoints that should not trigger redirect side effects
    // Check both with and without BASE_URL prefix
    const authEndpoints = ['/loginsso', '/login'];
    const isAuthEndpoint = authEndpoints.some(endpoint => {
      const fullPath = buildPath(endpoint);
      return currentPath === fullPath || currentPath.endsWith(endpoint) || currentPath.includes(endpoint);
    });

    // Skip for auth-related endpoints or if not authenticated
    if (isAuthEndpoint || !isAuthenticated) {
      if (isLoading) {
        setLoading(false);
      }
      if (
        currentPath !== loginPath
        && !currentPath.endsWith('/login')
        && !currentPath.includes('/loginsso')
      ) {
        useCompanyStore.getState().clearCompanySession();
        logoutUser();
        clearCpfHashStorage();
        clearEntraIdIdentity();
        window.location.href = loginPath;
      }
      return;
    }

    if (isLoading) {
      setLoading(false);
    }
    useCompanyStore.getState().clearCompanySession();
    logoutUser();
    clearCpfHashStorage();
    clearEntraIdIdentity();
    window.location.href = buildPath('/login');
  }, [isAuthenticated, isLoading, setLoading, logoutUser]);

  // Configure the redirect callback for 401
  useEffect(() => {
    apiService.setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  useEffect(() => {
    if (bootstrapSessionDone) {
      return;
    }

    // Skip global validateSession bootstrap on special auth/error flows.
    if (isAuthFlowPath(window.location.pathname)) {
      bootstrapSessionDone = true;
      setLoading(false);
      return;
    }

    if (!bootstrapSessionPromise) {
      bootstrapSessionPromise = (async () => {
        setLoading(true);
        try {
          const sessionOk = await validateSession();
          if (!sessionOk) {
            // Session is not recoverable: clear auth state.
            // REDIRECT LOGIN: ProtectedRoute will redirect after isAuthenticated becomes false.
            useCompanyStore.getState().clearCompanySession();
            logoutUser();
            clearCpfHashStorage();
            clearEntraIdIdentity();
          }
        } catch {
          useCompanyStore.getState().clearCompanySession();
          logoutUser();
          clearCpfHashStorage();
          clearEntraIdIdentity();
        } finally {
          bootstrapSessionDone = true;
          setLoading(false);
          bootstrapSessionPromise = null;
        }
      })();
    }
  }, [logoutUser, setLoading, validateSession]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,

    // Actions
    setAuthData,
    logout,
    updateUser,
    clearError,
    userDisplayName: resolveUserDisplayName(user, 'User')
  };
};
